import * as React from "react";

/**
 * Defers mounting of a heavy "overlay" surface (Tooltip content, ContextMenu
 * items, HoverCard portal, …) until the first time the user interacts with
 * the trigger. Once primed the overlay stays mounted — re-hovering or
 * re-focusing is a cheap state toggle on the already-mounted Radix root.
 *
 * Spreading `triggerProps` onto the trigger element wires up `onPointerEnter`,
 * `onFocus`, and `onContextMenu` to flip `primed` to `true`. Render the heavy
 * overlay only when `primed` is `true`.
 */
export interface LazyOverlay {
  primed: boolean;
  triggerProps: {
    onPointerEnter: () => void;
    onFocus: () => void;
    onContextMenu: () => void;
  };
}

export function useLazyOverlay(): LazyOverlay {
  const [primed, setPrimed] = React.useState(false);
  const prime = React.useCallback(() => setPrimed(true), []);
  const triggerProps = React.useMemo(
    () => ({
      onPointerEnter: prime,
      onFocus: prime,
      onContextMenu: prime,
    }),
    [prime],
  );
  return { primed, triggerProps };
}
